import DownloadIcon from "@mui/icons-material/Download"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import {
    Box,
    Checkbox,
    Collapse,
    FormControlLabel,
    Slider,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useTheme,
} from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { ReactElement, SyntheticEvent, useState } from "react"
import { useMatch, useNavigate, useSearchParams } from "react-router-dom"

import AppButton from "../../components/buttons/appButton"
import AppLoadingButton from "../../components/buttons/appLoadingButton"
import DataLoadError from "../../components/fallback/dataLoadError"
import DNOFilterPanelFrame from "../../components/fitlerPanel/dnoFilterPanelFrame"
import AppControlledDateRange from "../../components/inputs/controlled/appControlledDateRange"
import AppControlledSelect from "../../components/inputs/controlled/appControlledSelect"
import UserSuggestionAutocomplete from "../../components/inputs/controlled/userSuggestionAutocomplete"
import { LCT_TILES } from "../../components/submitApplication/single/selectLct/applicationTypeTiles"
import AppPaper from "../../components/surfaces/appPaper"
import ApplicationTable from "../../components/tables/applicationTable/applicationTable"
import {
    DnoAllResovedStauses,
    DnoApplicationTypes,
    DnoEscalationStatuses,
    EscalatedReasons,
} from "../../constants/enums"
import {
    APPLICATION_ID_KW,
    ASSIGNED_TO,
    POSTCODE,
    SLIDE_IN_TIMEOUT,
    STATUS,
    TECHNOLOGY,
    ADDRESS,
    ESCALATED_REASON,
    INSTALLER_EMAIL,
    MIN_GEN_CAPACITY,
    MAX_GEN_CAPACITY,
    MIN_MAX_DEMAND,
    MAX_MAX_DEMAND,
    DNO_DATE_FROM_KW,
    DNO_DATE_TO_KW,
    UPDATED_SINCE,
} from "../../constants/generalConstants"
import {
    TT_HELP_ALL_NOT_ESCALATED,
    TT_HELP_ESCALATED,
} from "../../constants/tooltips"
import { useApplicationDeriveFilters } from "../../hooks/useApplicationsDeriveFiltersFromUrl"
import useDownloadJson from "../../hooks/useDownloadJson"
import { useGetState } from "../../hooks/useGetState"
import { getShowDnoApplicationFilters } from "../../redux/preferencesSlice"
import {
    DNO_APPLICATIONS_ESCALATIONS,
    DNO_APPLICATIONS_NOTIFICATIONS,
} from "../../router/routes"
import { User } from "../../services/middleware/hooks/dnoInterface/protected/useGetAssigneeById"
import useGetDnoApplications from "../../services/middleware/hooks/dnoInterface/protected/useGetDnoApplications"
import {
    BUTTON_RADIUS_MULTIPLIER,
    GRID_SPACING,
} from "../../themes/defaultThemeParams"

/**
 * Lists applications for DNOs and enables filtering.
 */
const ListApplications = (): ReactElement => {
    // Api hooks
    const isEscalations = !!useMatch(DNO_APPLICATIONS_ESCALATIONS)
    const [sortKey, setSortKey] = useState<string>("")
    const [searchParams, setSearchParams] = useSearchParams()
    const {
        data,
        isError,
        isValidating,
        isLoading,
        downloadData,
        isFinalPage,
        page,
        setPage,
    } = useGetDnoApplications(searchParams, isEscalations, sortKey)
    const MAX_CAPACITY = 100
    const TOT_DEM_MAX_CAPACITY = 100
    // Support hooks
    const { downloadJson } = useDownloadJson()
    const showFilters = useGetState(getShowDnoApplicationFilters)
    const navigate = useNavigate()

    // Filter and sort hooks
    const {
        applicationId,
        setFilterFromTextfield,
        orderBy,
        orderDirection,
        dateFrom,
        dateTo,
        updatedSince,
        status,
        technology,
        postcode,
        setOrderBy,
        assignedTo,
        address,
        escalatedReason,
        installerEmail,
        genCapacityMin,
        genCapacityMax,
        totDemCapacityMin,
        totDemCapacityMax,
        isUnread,
        setFilterFromSlider,
    } = useApplicationDeriveFilters(searchParams, setSearchParams, true)
    // Local states only to be used sparingly - where possible rely on URL
    // params - this is to reduce renders
    const [localApplicationId, setLocalApplicationId] =
        useState<string>(applicationId)
    const [localPostcode, setLocalPostcode] = useState<string>(postcode || "")
    const [localAddress, setLocalAddress] = useState<string>(address || "")
    const [localInstallerEmail, setLocalInstallerEmail] = useState<string>(
        installerEmail || ""
    )
    const onSelectUserToFilter = (newValue: User | null): void => {
        setFilterFromTextfield(newValue?.username || "", ASSIGNED_TO)
    }

    const handleSortChange = (newSortKey: string): void => {
        setSortKey(newSortKey)
    }
    const theme = useTheme()
    const [localSliderValue, setLocalSliderValue] = useState<[number, number]>([
        genCapacityMin,
        genCapacityMax,
    ])
    const [localTotDemSliderValue, setLocalTotDemSliderValue] = useState<
        [number, number]
    >([totDemCapacityMin, totDemCapacityMax])
    const onSlide = (e_: Event, newValue: number | number[]): void => {
        setLocalSliderValue(newValue as [number, number])
    }
    const onTotDemSlide = (e_: Event, newValue: number | number[]): void => {
        setLocalTotDemSliderValue(newValue as [number, number])
    }
    const onGenCapacitySliderCommitted = (
        e: Event | SyntheticEvent<Element, Event>,
        value: number | number[]
    ): void => {
        setFilterFromSlider(e, value, [MIN_GEN_CAPACITY, MAX_GEN_CAPACITY])
    }
    const onTotDemSliderCommitted = (
        e: Event | SyntheticEvent<Element, Event>,
        value: number | number[]
    ): void => {
        setFilterFromSlider(e, value, [MIN_MAX_DEMAND, MAX_MAX_DEMAND])
    }
    const technologyMapping = LCT_TILES.map((tile) => ({
        label: tile.title,
        value: tile.value,
    }))
    const techOptions: string[] = LCT_TILES.map((tile) => tile.title)

    const escalationMapping = EscalatedReasons.map((tile) => ({
        label: tile.title,
        value: tile.value,
    }))
    const escalationOptions: string[] = EscalatedReasons.map(
        (tile) => tile.title
    )
    return (
        <Grid container spacing={GRID_SPACING}>
            <DNOFilterPanelFrame
                applicationId={applicationId}
                isValidating={isValidating}
                all={!isEscalations}
                heading={isEscalations ? "Escalated" : "Resolved"}
                applications={
                    isEscalations
                        ? "Applications Awaiting Your Review"
                        : "Applications"
                }
                tooltip={
                    isEscalations
                        ? TT_HELP_ESCALATED
                        : TT_HELP_ALL_NOT_ESCALATED
                }
                colour={
                    isEscalations
                        ? theme.palette.primary.main
                        : theme.palette.success.main
                }
                isDnoMode
                clearLocalAddress={() => {
                    setLocalAddress("")
                    setFilterFromTextfield("", ADDRESS)
                }}
                clearLocalPostcode={() => {
                    setLocalPostcode("")
                    setFilterFromTextfield("", POSTCODE)
                }}
                clearLocalInstallerEmail={() => {
                    setLocalInstallerEmail("")
                    setFilterFromTextfield("", INSTALLER_EMAIL)
                }}
                clearLocalGenCapacity={() => {
                    setLocalSliderValue([0, MAX_CAPACITY])
                    setFilterFromSlider(
                        {} as SyntheticEvent<Element, Event>,
                        [0, MAX_CAPACITY],
                        [MIN_GEN_CAPACITY, MAX_GEN_CAPACITY]
                    )
                }}
                clearLocalDemCapacity={() => {
                    setLocalTotDemSliderValue([0, TOT_DEM_MAX_CAPACITY])
                    setFilterFromSlider(
                        {} as SyntheticEvent<Element, Event>,
                        [0, MAX_CAPACITY],
                        [MIN_MAX_DEMAND, MAX_MAX_DEMAND]
                    )
                }}
            />
            <Grid xs={12}>
                <AppPaper>
                    <Collapse in={showFilters} timeout={SLIDE_IN_TIMEOUT}>
                        <Grid
                            container
                            spacing={GRID_SPACING}
                            marginBottom={4}
                            marginTop={1}
                            columns={12}
                            display="flex"
                            alignItems="center"
                        >
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <ToggleButtonGroup
                                    color="primary"
                                    fullWidth
                                    value={
                                        isEscalations
                                            ? DnoApplicationTypes.escalationsIncCx
                                            : DnoApplicationTypes.allApprovedIncCx
                                    }
                                    exclusive
                                    onChange={(_e, v) => {
                                        const to =
                                            v ===
                                            DnoApplicationTypes.escalationsIncCx
                                                ? DNO_APPLICATIONS_ESCALATIONS
                                                : DNO_APPLICATIONS_NOTIFICATIONS
                                        searchParams.delete(STATUS)
                                        navigate(
                                            `${to}${
                                                searchParams.toString()
                                                    ? `?${searchParams.toString()}`
                                                    : ""
                                            }`
                                        )
                                    }}
                                >
                                    <ToggleButton
                                        value={
                                            DnoApplicationTypes.escalationsIncCx
                                        }
                                        sx={{
                                            borderRadius:
                                                BUTTON_RADIUS_MULTIPLIER,
                                            textTransform: "none",
                                        }}
                                    >
                                        Escalations
                                    </ToggleButton>
                                    <ToggleButton
                                        value={
                                            DnoApplicationTypes.allApprovedIncCx
                                        }
                                        sx={{
                                            borderRadius:
                                                BUTTON_RADIUS_MULTIPLIER,
                                            textTransform: "none",
                                        }}
                                    >
                                        All
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <TextField
                                    label="Application ID"
                                    value={localApplicationId}
                                    placeholder="Full application ID required..."
                                    onChange={(event) => {
                                        setLocalApplicationId(
                                            event.target.value
                                        )
                                        setFilterFromTextfield(
                                            event.target.value,
                                            APPLICATION_ID_KW
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledDateRange
                                    label="Date from"
                                    dateValue={dateFrom}
                                    setDateValue={(v: string) =>
                                        setFilterFromTextfield(
                                            v,
                                            DNO_DATE_FROM_KW
                                        )
                                    }
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledDateRange
                                    label="Date to"
                                    dateValue={dateTo}
                                    setDateValue={(v: string) =>
                                        setFilterFromTextfield(
                                            v,
                                            DNO_DATE_TO_KW
                                        )
                                    }
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledDateRange
                                    label="Last Updated"
                                    dateValue={updatedSince}
                                    setDateValue={(v: string) =>
                                        setFilterFromTextfield(v, UPDATED_SINCE)
                                    }
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledSelect
                                    value={status}
                                    options={Object.values(
                                        isEscalations
                                            ? DnoEscalationStatuses
                                            : DnoAllResovedStauses
                                    )}
                                    label="Status"
                                    setValue={(
                                        e_: SyntheticEvent<Element, Event>,
                                        nv: string | number | null
                                    ) =>
                                        setFilterFromTextfield(
                                            nv as string,
                                            STATUS
                                        )
                                    }
                                    loading={false}
                                    fullWidth
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledSelect
                                    value={technology}
                                    options={techOptions}
                                    label="Technology"
                                    setValue={(
                                        _event: SyntheticEvent<Element, Event>,
                                        newValue: string | number | null
                                    ) => {
                                        const selectedTile =
                                            technologyMapping.find(
                                                (item) =>
                                                    item.label === newValue
                                            )
                                        setFilterFromTextfield(
                                            selectedTile?.value || "",
                                            TECHNOLOGY
                                        )
                                    }}
                                    loading={false}
                                    fullWidth
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <AppControlledSelect
                                    value={escalatedReason}
                                    options={escalationOptions}
                                    label="Escalated Reason"
                                    setValue={(
                                        _event: SyntheticEvent<Element, Event>,
                                        newValue: string | number | null
                                    ) => {
                                        const selectedTile =
                                            escalationMapping.find(
                                                (item) =>
                                                    item.label === newValue
                                            )
                                        setFilterFromTextfield(
                                            selectedTile?.value || "",
                                            ESCALATED_REASON
                                        )
                                    }}
                                    loading={false}
                                    fullWidth
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <TextField
                                    label="Installer Email"
                                    value={localInstallerEmail}
                                    placeholder="Enter Installers email..."
                                    onChange={(event) => {
                                        setLocalInstallerEmail(
                                            event.target.value
                                        )
                                        setFilterFromTextfield(
                                            event.target.value,
                                            INSTALLER_EMAIL
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <TextField
                                    label="Postcode"
                                    value={localPostcode}
                                    placeholder="Enter postcode..."
                                    onChange={(event) => {
                                        setLocalPostcode(
                                            event.target.value.toUpperCase()
                                        )
                                        setFilterFromTextfield(
                                            event.target.value,
                                            POSTCODE
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <TextField
                                    label="Address"
                                    value={localAddress}
                                    placeholder="Enter address..."
                                    onChange={(event) => {
                                        setLocalAddress(event.target.value)
                                        setFilterFromTextfield(
                                            event.target.value,
                                            ADDRESS
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <UserSuggestionAutocomplete
                                    onSelectedUserChange={onSelectUserToFilter}
                                    userId={assignedTo}
                                    filter
                                />
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <Typography variant="body1" gutterBottom>
                                    {`Generation Capacity
                        : ${localSliderValue[0]} - ${localSliderValue[1]}${
                            localSliderValue[1] === MAX_CAPACITY ? "+" : ""
                        } kW`}
                                </Typography>
                                <Box
                                    sx={{ width: "90%", justifySelf: "center" }}
                                >
                                    <Slider
                                        value={localSliderValue}
                                        max={MAX_CAPACITY}
                                        onChange={onSlide}
                                        onChangeCommitted={
                                            onGenCapacitySliderCommitted
                                        }
                                        valueLabelDisplay="auto"
                                        getAriaLabel={(index: number) =>
                                            `capacitySlider${index}`
                                        }
                                        marks={[
                                            { value: 0, label: "0" },
                                            {
                                                value: MAX_CAPACITY,
                                                label: `${MAX_CAPACITY}`,
                                            },
                                        ]}
                                    />
                                </Box>
                            </Grid>
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <Typography variant="body1" gutterBottom>
                                    {`Total demand
                        : ${localTotDemSliderValue[0]} - ${
                            localTotDemSliderValue[1]
                        }${
                            localTotDemSliderValue[1] === TOT_DEM_MAX_CAPACITY
                                ? "+"
                                : ""
                        } A`}
                                </Typography>
                                <Box
                                    sx={{ width: "90%", justifySelf: "center" }}
                                >
                                    <Slider
                                        value={localTotDemSliderValue}
                                        max={TOT_DEM_MAX_CAPACITY}
                                        onChange={onTotDemSlide}
                                        onChangeCommitted={
                                            onTotDemSliderCommitted
                                        }
                                        valueLabelDisplay="auto"
                                        getAriaLabel={(index: number) =>
                                            `capacitySlider${index}`
                                        }
                                        marks={[
                                            { value: 0, label: "0" },
                                            {
                                                value: TOT_DEM_MAX_CAPACITY,
                                                label: `${TOT_DEM_MAX_CAPACITY}`,
                                            },
                                        ]}
                                    />
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isUnread}
                                            onChange={(e) => {
                                                setFilterFromTextfield(
                                                    String(e.target.checked),
                                                    "isUnread"
                                                )
                                            }}
                                        />
                                    }
                                    label="Any Unread Comments?"
                                />
                            </Grid>
                            <Grid
                                xl={6}
                                md={6}
                                sm={12}
                                xs={12}
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    pr: 2,
                                }}
                            >
                                <AppButton
                                    variant="contained"
                                    endIcon={<DownloadIcon />}
                                    disabled={
                                        isLoading || isError || !downloadData
                                    }
                                    onClick={() =>
                                        downloadData &&
                                        downloadJson(downloadData, "results")
                                    }
                                >
                                    Download
                                </AppButton>
                            </Grid>
                        </Grid>
                    </Collapse>

                    {isError ? (
                        <DataLoadError />
                    ) : (
                        <Stack spacing={GRID_SPACING}>
                            <ApplicationTable
                                searchResults={data}
                                orderBy={orderBy}
                                setOrderBy={setOrderBy}
                                orderDirection={orderDirection}
                                isLoading={isLoading}
                                isEscalations={isEscalations}
                                onSortChange={handleSortChange}
                            />
                            {data && (
                                <Stack
                                    alignItems="center"
                                    justifyContent="center"
                                    direction="row"
                                    width="100%"
                                >
                                    {/* final page */}
                                    {!isFinalPage && (
                                        <AppLoadingButton
                                            color="secondary"
                                            endIcon={<MoreHorizIcon />}
                                            loading={isValidating}
                                            loadingPosition="end"
                                            onClick={() => setPage(page + 1)}
                                            sx={{
                                                width: "20%",
                                                mt: "6px",
                                            }}
                                        >
                                            See More
                                        </AppLoadingButton>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    )}
                </AppPaper>
            </Grid>
        </Grid>
    )
}

export default ListApplications
